import type { Attributes, GenerationEvent } from "../types.js";

/**
 * Build OTEL span attributes following the GenAI semantic conventions.
 * Langfuse auto-detects these as LLM generations.
 *
 * Maps {@link GenerationEvent} fields to standard `gen_ai.*` keys (model,
 * provider, token counts, stop reason) and Skaile-specific `skaile.generation.*`
 * keys (duration, cost, prompt preview). Only defined fields are included in
 * the returned map — undefined optional fields are omitted.
 *
 * @param gen - The generation event to convert to OTEL attributes.
 * @returns A flat {@link Attributes} map ready to pass to `otelSpan.setAttributes()`.
 * @see https://opentelemetry.io/docs/specs/semconv/gen-ai/
 * @docLink packages/telemetry/concepts#otel-telemetry-provider
 */
export function generationAttributes(gen: GenerationEvent): Attributes {
  const attrs: Attributes = {
    "gen_ai.system": gen.provider,
    "gen_ai.request.model": gen.model,
    "skaile.generation.duration_ms": gen.durationMs,
  };

  if (gen.inputTokens !== undefined) {
    attrs["gen_ai.usage.input_tokens"] = gen.inputTokens;
  }
  if (gen.outputTokens !== undefined) {
    attrs["gen_ai.usage.output_tokens"] = gen.outputTokens;
  }
  if (gen.totalTokens !== undefined) {
    attrs["gen_ai.usage.total_tokens"] = gen.totalTokens;
  }
  if (gen.stopReason) {
    attrs["gen_ai.response.finish_reasons"] = gen.stopReason;
  }
  if (gen.costUsd !== undefined) {
    attrs["skaile.generation.cost_usd"] = gen.costUsd;
  }
  if (gen.promptPreview) {
    attrs["skaile.generation.prompt_preview"] = gen.promptPreview;
  }

  return attrs;
}
